import { Eye, EyeOff } from "lucide-react";
import {
  CHECKBOX_TYPES,
  CURRENCY,
  NUMBER,
  DATE_TYPES,
  DROPDOWN_TYPES,
  EIN,
  PERCENT,
  PHONE_NUMBER,
  RADIO,
  SSN,
  HYPERLINK,
  MULTILINE_TEXTFIELD,
  DROPDOWN_WITH_TEXTFIELD,
  CONTAINER,
  DUMMY,
  ONE_TO_MANY_RADIO,
  DROPDOWN_TYPES_MONTHS,
  HORIZONTAL_LINE,
} from "../../../utils/InputFieldConfig";
import {
  checkBoxReadableValues,
  getDropDownLabel,
  maskSSN,
  getDropDownText,
  RadioMultiSupport,
  getMonthsLabels,
  formatEIN,
  formatSSN,
} from "../label-utils/label-utils";
import { ContainerInput } from "../container-input/container-input";
import { displayOnlyYear, humanReadableDate } from "../../../utils/dateHelper";
import { roundUpCurrency } from "../../../utils/utils";
import { HorizontalLineField } from "../horizontal-line/horizontal-line-field";

const hasValue = (value) => value || value === 0;

const getDefaultValue = (fieldValue) => {
  return hasValue(fieldValue) ? fieldValue : "--";
};

const renderSSNField = (
  fieldValue,
  item,
  readOnly,
  handleFieldLevelHandler,
  currentPiiInfoFields,
) => {
  const isMasked = currentPiiInfoFields?.ssn;
  const displayValue = isMasked ? maskSSN(fieldValue) : formatSSN(fieldValue);
  const Icon = isMasked ? EyeOff : Eye;

  return (
    <span className="inline-flex items-center gap-2">
      <span className={displayValue === "--" ? "" : "font-mono tracking-wider"}>
        {displayValue}
      </span>
      {!readOnly && (
        <button
          type="button"
          onClick={() => handleFieldLevelHandler?.(item, isMasked)}
          className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
          aria-label={isMasked ? "Hide SSN" : "Show SSN"}
        >
          <Icon className="h-4 w-4 text-gray-600" />
        </button>
      )}
    </span>
  );
};

const renderHyperlink = (fieldValue, handleClick) => {
  if (!fieldValue) return "--";
  return (
    <a
      href={`${fieldValue}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="text-gray-900 underline underline-offset-2 hover:text-gray-700"
    >
      {fieldValue}
    </a>
  );
};

const renderCurrency = (fieldValue, item) => {
  if (!hasValue(fieldValue)) return "--";
  const digits = item?.roundOffToTwoDecimal ? 2 : 0;
  return roundUpCurrency(fieldValue, digits) || "--";
};

const renderNumber = (fieldValue) => {
  return hasValue(fieldValue) ? Number(fieldValue)?.toLocaleString() : "--";
};

const renderPercent = (fieldValue, item) => {
  if (!hasValue(fieldValue)) return "--";
  const precision = item?.percentAcceptedLimit || 2;
  return `${Number(fieldValue)?.toFixed(precision)}%`;
};

const renderMultilineText = (fieldValue) => (
  <span className="block max-w-full whitespace-pre-wrap break-words leading-relaxed">
    {fieldValue || "--"}
  </span>
);

const renderContainer = (
  item,
  readOnly,
  componentContext,
  onFieldChange,
  fieldValues,
) => (
  <ContainerInput
    readOnly={readOnly}
    fields={item.fields}
    componentContext={componentContext}
    onFieldChange={onFieldChange}
    fieldValues={fieldValues?.[item.uniqueField]}
  />
);

const renderDateField = (fieldValue, item) => {
  return item.onlyYears
    ? displayOnlyYear(fieldValue)
    : humanReadableDate(fieldValue);
};

const createFieldHandlers = (context) => ({
  [DROPDOWN_TYPES]: () =>
    getDropDownLabel(
      context.item.values || context.item.options,
      context.fieldValue,
    ),
  [DATE_TYPES]: () => renderDateField(context.fieldValue, context.item),
  [ONE_TO_MANY_RADIO]: () =>
    RadioMultiSupport(context.item.values, context.fieldValue),
  [CHECKBOX_TYPES]: () => checkBoxReadableValues(context.fieldValue),
  [RADIO]: () => checkBoxReadableValues(context.fieldValue),
  [SSN]: () =>
    renderSSNField(
      context.fieldValue,
      context.item,
      context.readOnly,
      context.handleFieldLevelHandler,
      context.currentPiiInfoFields,
    ),
  [HYPERLINK]: () => renderHyperlink(context.fieldValue, context.handleClick),
  [CURRENCY]: () => renderCurrency(context.fieldValue, context.item),
  [EIN]: () => (context.fieldValue ? formatEIN(context.fieldValue) : "--"),
  [NUMBER]: () => renderNumber(context.fieldValue),
  [PERCENT]: () => renderPercent(context.fieldValue, context.item),
  [PHONE_NUMBER]: () => context.fieldValue || "--",
  [MULTILINE_TEXTFIELD]: () => renderMultilineText(context.fieldValue),
  [DROPDOWN_WITH_TEXTFIELD]: () =>
    getDropDownText(context.fieldValue, context.isTooltip),
  [DROPDOWN_TYPES_MONTHS]: () => getMonthsLabels(context.fieldValue),
  [CONTAINER]: () =>
    renderContainer(
      context.item,
      context.readOnly,
      context.componentContext,
      context.onFieldChange,
      context.fieldValues,
    ),
  [HORIZONTAL_LINE]: () => <HorizontalLineField />
});

export const LabelFields = (
  fieldValue,
  item,
  error,
  onFieldChange,
  classes,
  readOnly,
  fieldValues,
  componentContext,
  handleFieldLevelHandler,
  currentPiiInfoFields,
  isTooltip = false,
) => {
  const handleClick = (event) => {
    event.preventDefault();
    if (!fieldValue) return;
    const url = String(fieldValue).startsWith("http")
      ? String(fieldValue)
      : `https://${fieldValue}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const context = {
    fieldValue,
    item,
    readOnly,
    fieldValues,
    componentContext,
    handleFieldLevelHandler,
    currentPiiInfoFields,
    isTooltip,
    onFieldChange,
    handleClick,
  };

  const fieldType = item?.type?.toLowerCase();
  const handlers = createFieldHandlers(context);
  const handler = handlers[fieldType];

  if (handler) {
    return handler();
  }

  return getDefaultValue(fieldValue);
};
